import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import ValuesTabs from '../components/ValuesTabs'
import ScrollToTopButton from '../components/ScrollToTopButton'

const ABOUT_LOGO = '/logo.webp'


const AboutSection = () => {
  const { t } = useTranslation()

  return (
    <section id="about" className="bg-white text-black" aria-labelledby="about-heading" data-section="about">
      <div className="border-b border-black/10 bg-white">
        <div className="mx-auto flex min-h-screen max-w-6xl flex-col gap-12 px-4 py-16 sm:px-6 sm:flex-row sm:items-center lg:px-8 lg:py-24">
          <div className="flex-1 space-y-8 order-2 sm:order-1">
            <div className="relative inline-block overflow-hidden w-full max-w-2xl">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.3 }}
                className="absolute left-0 top-0 h-full bg-primary z-0"
              />
              <div className="px-8 py-10 relative z-10">
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight">{t('about.vision_heading')}</h1>
                <p className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase">{t('about.vision_subheading')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center sm:justify-start order-1 sm:order-2">
            <div className="hero-logo-card-no-shadow w-60 sm:w-80 md:w-[22rem]">
              <img src={ABOUT_LOGO} alt={t('about.logo_alt')} className="w-full h-full object-contain brightness-0" loading="eager" />
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen py-20 px-4 sm:px-6 lg:px-8 bg-[#f5f5f5]">
        <div className="mx-auto max-w-6xl h-full flex items-center">
          <div className="grid gap-12 lg:grid-cols-[1fr_2fr] w-full">
            <div className="relative inline-block self-start overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.3 }}
                className="absolute left-0 top-0 h-full bg-primary z-0"
              />
              <h2 id="about-heading" className="text-3xl font-bold uppercase px-6 py-4 relative z-10">
                {t('about.about_heading')}
              </h2>
            </div>
            <div className="space-y-6 text-xl sm:text-2xl leading-relaxed text-gray-700">
              {t('about.about_paragraphs', { returnObjects: true }).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white min-h-screen py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl h-full flex items-center">
          <div className="grid gap-12 lg:grid-cols-[1fr_2fr] w-full">
            <div className="relative inline-block self-start overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.3 }}
                className="absolute left-0 top-0 h-full bg-primary z-0"
              />
              <h2 className="text-3xl font-bold uppercase text-black px-6 py-4 relative z-10">{t('about.mission_heading')}</h2>
            </div>
            <div className="space-y-6 text-xl sm:text-2xl leading-relaxed text-gray-700">
              {t('about.mission_paragraphs', { returnObjects: true }).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ValuesTabs />

      <div className="bg-white min-h-screen py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center h-full justify-center">
          <div className="relative flex w-full flex-col items-center">
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.3 }}
              className="bg-primary flex items-center justify-center px-16 py-20"
              style={{ aspectRatio: '2/5', maxWidth: '380px', width: '100%' }}
            >
              <p className="text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-[0.05em] text-black text-center leading-tight px-10">{t('about.purpose_heading')}</p>
            </motion.div>
            <p className="absolute bottom-80 left-1/2 -translate-x-1/2 translate-y-1/2 text-xl sm:text-3xl font-bold leading-relaxed text-black text-center max-w-4xl z-10">
              {t('about.purpose_text')}
            </p>
          </div>
        </div>
      </div>

      <ScrollToTopButton />
    </section>
  )
}

export default AboutSection
